import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { OlxCategory } from 'src/app/modules/crm/power-olx-v2/interfaces/power-olx-v2.interface';
import { ModalService } from '../../../../../../../services/modal.service';
import { Subject, takeUntil } from 'rxjs';
import { SelectCategoryComponent } from '../../../../../../../dialogs/select-category/select-category.component';
import { SelectedCategoryEvent } from './advert-category-picker.interface';

@Component({
  selector: 'app-advert-category-picker',
  templateUrl: './advert-category-picker.component.html',
  styleUrl: './advert-category-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AdvertCategoryPickerComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<void> = new Subject<void>();
  private _categories: OlxCategory[] = [];

  @Input() categoryControl!: FormControl<number | null>;
  @Input() isCanPick: boolean = true;
  @Input() loader: boolean = false;

  @Output() categoryChanged = new EventEmitter<SelectedCategoryEvent>();

  protected selectedCategory?: OlxCategory;
  protected selectedCategoryPathText?: string;

  @Input()
  set categories(value: OlxCategory[]) {
    this._categories = value;

    if (this.categoryControl) {
      this.setSelectedCategory(this.categoryControl.value);
    }
  }

  get categories(): OlxCategory[] {
    return this._categories;
  }

  constructor(private readonly modalService: ModalService) {}

  ngOnInit(): void {
    this.setSelectedCategory(this.categoryControl?.value);

    this.categoryControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.setSelectedCategory(value);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setSelectedCategory(categoryId: number | null): void {
    if (!categoryId) return;

    const found = this.categories?.find((c) => c.category_id === categoryId);
    if (!found) return;

    this.selectedCategory = found;
    this.selectedCategoryPathText = this.buildCategoryPathText(found);

    this.categoryChanged.emit({
      selectedCategory: found,
      selectedCategoryPathText: this.selectedCategoryPathText,
    });
  }

  protected onSelectCategory(): void {
    const modalData = {
      title: 'select_category',
      component: SelectCategoryComponent,
      translate: true,
      data: this.categories,
    };

    this.modalService.openModal<OlxCategory[]>(modalData).subscribe((categoryId: number) => {
      if (!categoryId) return;

      this.categoryControl.setValue(categoryId);
    });
  }

  private buildCategoryPathText(category: OlxCategory): string {
    const path: string[] = [];
    let current = this.categories.find((c) => c.category_id === category.parent_id)!;
    while (current) {
      path.unshift(current.name);
      current = this.categories.find((c) => c.category_id === current.parent_id)!;
    }
    return path.join(' / ');
  }
}
